import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { useDispatch, useSelector } from 'react-redux';
import { push, replace, goBack } from 'redux-first-history';
import { Navigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { jobsActions, jobsSelectors } from '../../redux/jobs';
import { loginActions } from '../../redux/login';
import { applicationSelectors } from '../../redux/application';
import { MeterTaskResultType } from '../../redux/jobs/jobs.types';
import { mergeJobConfig } from '../../helper/JobConfigHelper';
import { confirmStepCount, resolveConfirmValidationFeatures, validateConfirm } from './helper/confirmValidation';
import { toReadingImagePayload } from '../../helper/ReadingImagePayloadHelper';
import { AI_CAMERA_SCREENSHOT_QUALITY, AI_IMAGE_MAX_DIMENSION } from '../../helper/ImageFileHelper';
import aiApi from '../../api/ai.api';
import RemoteResultHelper, { cropImage } from '../../helper/RemoteResultHelper';
import { RelBox } from '../../api/ai.api.types';
import { RemoteResult } from '../common/RemoteResultCommand/index.types';
import { aiReadingReducer, initialAiReadingState } from './aiReadingMachine';
import { useLiveDetection } from './hooks/useLiveDetection';
import { useSubmitOutcome } from './hooks/useSubmitOutcome';
import { useBoxTracker } from './hooks/useBoxTracker';
import { EARCON_TO_SPEECH_MS, useAudioFeedback } from './hooks/useAudioFeedback';
import { captureFrame, captureViewportRegion, visibleViewport } from './helper/frameCapture';
import {
    BoxRelation,
    applyBoxRelation,
    computeBoxRelation,
    expandRelBox,
    mapResultBoxesFromCrop,
    shiftBoxBy,
    shouldAdoptHiResBox,
} from './helper/overlayGeometry';
import {
    computeAlignment,
    computeSnap,
    computeReadingGate,
    computeReadingGateScore,
    computeTargetFrame,
    emphasizedScore,
    rampColor,
} from './helper/alignmentGuidance';
import {
    AI_HIRES_BOX_ADOPT_MIN_IOU,
    AI_HIRES_CROP_INTERVAL_MS,
    AI_HIRES_CROP_JPEG_QUALITY,
    AI_HIRES_CROP_MAX_DIMENSION,
    AI_HIRES_CROP_PADDING_FACTOR,
    AI_HIRES_MIN_BOX_AREA,
} from './helper/liveConstants';
import {
    buildNoMatchInfo,
    deriveExpectedMeter,
    ExpectedMeter,
    jobsIncludeMedium,
    matchMeterNumberToJobs,
    normalizeMeterNumber,
    resultIdentifiesMeter,
} from './helper/meterMatching';
import { NoMatchInfo } from './aiReadingMachine';
import { AccumulatorState, accumulate, initAccumulator, isComplete, missingRegisters } from './helper/registerAccumulator';
import { DEFAULT_TRACKER_SETTINGS, TrackerSettings } from './helper/trackerSettings';
import { computeDetectionPhase } from './helper/detectionPhase';
import { stepInstructionDelayMs } from './helper/instructionTiming';
import OfflinePanel from './components/OfflinePanel';
import DonePanel from './components/DonePanel';
import AlignmentGuide from './components/AlignmentGuide';
import FocusBlur from './components/FocusBlur';
import DetectionOverlay from './components/DetectionOverlay';
import TrackerDebugOverlay from './components/TrackerDebugOverlay';
import GuidancePanel, { instructionKeyFor, instructionParamsFor } from './components/GuidancePanel';
import ConsentPanel from './components/ConsentPanel';
import CandidatePicker from './components/CandidatePicker';
import MeterNotFoundPanel from './components/MeterNotFoundPanel';
import ConfirmReadingsPanel from './components/ConfirmReadingsPanel';
import { resolveMeterType } from '../../helper/MeterHelper';
import Icon from '../../components/envago/Icon/Icon';
import Button from '../../components/envago/Button/Button';
import { isMobilePlatform } from '../../helper/PlatformHelper';

// Number of consecutive failed meter-number match attempts in the 'identify' step before we
// give up and surface the "meter not found" panel instead of retrying forever. 2 (down from 3):
// every attempt already sits BEHIND the OCR consensus (the same number read twice in a row), so
// random misreads are filtered before the first attempt — and re-matching the same consensus
// number against the same jobs is deterministic; the second attempt only exists to catch the
// rare twice-identical misread being corrected by a later read.
const NO_MATCH_ATTEMPTS_BEFORE_FAILED = 2;
// Wrong-medium fast path: consecutive gated results whose recognized medium exists in NO job at
// all (e.g. a gas meter while only electricity readings are due) before the noMatch panel is
// surfaced with that explanation — without waiting for a readable meter number or the 35s timeout.
const WRONG_MEDIUM_ATTEMPTS_BEFORE_FAILED = 3;
// NOTE: there is deliberately NO automatic timeout out of the 'tariffs' step — and (product
// decision 2026-07-08) no "continue manually" escape button anymore either. The job (and thus
// the full set of required registers) is always known in this flow, so ALL of them must be
// captured — auto-advancing with gaps would normalize incomplete readings. Instead, when the
// capture makes no progress for TARIFF_STUCK_PROMPT_MS, a non-blocking exit panel offers to
// continue manually inside the matched job (see captureStuck below); recognition keeps running
// behind it, so the flow can still complete on its own.
const TARIFF_STUCK_PROMPT_MS = 30000;
// Escape hatch for the identify step: when no meter number becomes readable at all (e.g. the AI
// keeps returning id=null), no-match attempts never accumulate and the live loop would poll
// forever. After this time the meter-not-found panel is surfaced (which also pauses polling).
// Two tiers: while the user is still aiming (reading gate closed) the long timeout applies —
// the escape hatch must not fire during the approach. Once the gate is OPEN, hi-res reads run
// every ~1.5-2s, so "still nothing readable after 15s" is a solid verdict; waiting the full 35s
// in front of an unreadable (usually wrong) meter felt endless in the field.
const IDENTIFY_TIMEOUT_MS = 35000;
const IDENTIFY_GATED_TIMEOUT_MS = 15000;
// Escape hatch for the framing step: without any meter sighting the live loop would poll the
// fast-detect endpoint forever (there is no counter that could accumulate). After this long
// without a single detection the timeout panel is surfaced; its blocker pauses the live loop,
// so no further frames are sent until the user explicitly retries. Every sighting re-arms the
// timer (see the effect below) — the timeout only fires after 10s of NOTHING.
const FRAMING_TIMEOUT_MS = 10000;
// Results seen during framing/identify already contain reading values; they are buffered here and
// replayed into the accumulator as soon as the job (and thus its expected registers) is known, so
// early recognitions are not thrown away. A small cap keeps only the most recent ones.
const EARLY_RESULTS_BUFFER_SIZE = 10;
// Step-instruction debounce and the camera-permission special case live in ./helper/instructionTiming
// (stepInstructionDelayMs) so the timing is unit-tested in isolation.
// Re-snap flutter at the alignment threshold must not turn into a clicking noise.
const SNAP_EARCON_MIN_INTERVAL_MS = 1500;

const LIVE_LOOP_STEPS = new Set(['framing', 'identify', 'tariffs', 'read']);
const TRACKER_STEPS = new Set(['framing', 'identify', 'tariffs', 'read']);
// Steps in which high-res crop reads run ("detect small, read large"): the live loop keeps
// localizing on small frames, while a native-resolution crop of the tracked meter box feeds the
// actual recognition (meter number, register values). Not needed during framing.
const HIRES_READ_STEPS = new Set(['identify', 'tariffs', 'read']);
// NOTE: on-device DataMatrix decoding (zxing) was removed entirely (2026-07-10): the AI
// detection already returns the meter number decoded from the meter's DataMatrix/QR code (a
// confidence of 1.0 means an exact code match, not OCR), so a second local decode was redundant
// — and the raw envago sticker envelope it yielded even jammed the identify step. See git
// history (dataMatrixScanner.ts) if an on-device path is ever needed again.

// Continuous emphasized ramp while aiming (white→green, quadratic emphasis so the shift
// accelerates as alignment improves), saturated green + glow once snapped — the snap kick
// (thicker border + box-shadow on DetectionOverlay) is what makes the "now it reads" moment
// unmistakable, not a hard color cutover.
const SNAPPED_ACCENT = 'rgb(74, 222, 128)'; // tailwind green-400 == rampColor(1)

const AiReadingScreen = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    // Leaving the assistant returns to wherever it was opened from. The entry points open the
    // assistant with push('/ai-ablesung', {from}), so `from` is already the previous stack entry:
    // a plain goBack pops the assistant off in ONE Back press. replace(from) would instead DUPLICATE
    // that entry, so the first Back press would land on an identical screen and appear to do nothing
    // (issue #50). Without a `from` (deep link, no in-app predecessor) fall back to replace('/').
    const exitAssistant = useCallback(() => {
        const from = (location.state as { from?: string } | undefined)?.from;
        if (from && from !== '/ai-ablesung') {
            dispatch(goBack());
        } else {
            dispatch(replace('/'));
        }
    }, [location.state, dispatch]);
    const webcamRef = useRef<Webcam>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const aiLiveAssistantEnabled = useSelector(applicationSelectors.isAiLiveAssistantEnabled);
    const jobsFromStore = useSelector(jobsSelectors.getJobs);
    const jobs = useMemo(() => jobsFromStore ?? [], [jobsFromStore]);
    const config = useSelector(applicationSelectors.getAiLiveAssistantConfig);
    const trailingChars = useSelector(applicationSelectors.getAiImageEvaluationMeterNumberMatchTrailingCharsCount);
    const abortAfterMs = useSelector(applicationSelectors.getAiImageEvaluationAbortAfterMs);
    const maskPostDecimals = useSelector(applicationSelectors.isMaskPostDecimalsEnabled);

    const [machine, dispatchMachine] = useReducer(aiReadingReducer, initialAiReadingState);

    // The done panel only follows the server's verdict (qa#128): accepted → done, queued while
    // offline → done with the "sent once online" wording, rejected → stay in the confirm step
    // (the jobs saga already shows "Senden fehlgeschlagen"; the values stay for a retry).
    const [submitQueued, setSubmitQueued] = useState(false);
    const { sending: submitting, track: trackSubmit } = useSubmitOutcome((outcome) => {
        if (outcome === 'error') {
            return;
        }
        setSubmitQueued(outcome === 'pending');
        dispatchMachine({ type: 'CONFIRMED' });
    });
    const [accumulator, setAccumulator] = useState<AccumulatorState>(() => initAccumulator([]));
    const [lastDetection, setLastDetection] = useState<{ meterBox?: RelBox; displayBox?: RelBox; capturedAt?: number } | null>(null);
    const noMatchCountRef = useRef(0);
    // Wrong-medium fast path counter + the most recent explanation of WHY matching failed; the
    // latter is attached to MATCH_FAILED so panel/announcement can be specific.
    const wrongMediumCountRef = useRef(0);
    const lastNoMatchInfoRef = useRef<NoMatchInfo | null>(null);
    // OCR meter-number consensus: the same normalized number must be read `consensusCount` times
    // in a row before a job match is attempted (random misreads don't repeat identically).
    const ocrConsensusRef = useRef<{ number: string; count: number } | null>(null);
    const earlyResultsRef = useRef<Array<{ result: RemoteResult; frame: string; frameRegion?: RelBox }>>([]);
    // Last known display↔meter geometry; lets a display-only detection still yield a meter box.
    const meterDisplayRelationRef = useRef<BoxRelation | null>(null);
    // The tracked overlay frame is owned by the fast live loop; this holds its most recent meter
    // box so a hi-res crop read can be checked against it (IoU gate) before it may refine the frame.
    const lastFastBoxRef = useRef<RelBox | null>(null);
    // Fast-loop cadence signals — forward-declared here because useLiveDetection is called above
    // useBoxTracker / the reading gate. Assigned further down once those values exist; read only
    // asynchronously inside the poll loop, so the current values are always available by call time.
    const trackerModeRef = useRef<'tracking' | 'snap'>('snap');
    const trackedBoxRef = useRef<RelBox | null>(null);
    const readingGateRef = useRef(false);
    const getPhase = useCallback(() => computeDetectionPhase(trackerModeRef.current, trackedBoxRef.current, readingGateRef.current), []);
    const fullReadsStartedRef = useRef(false);
    // Anti-cheat identity guard (Task 13): the meter number/tolerance the matched job identifies
    // as, so onLiveResult can reject results that read a DIFFERENT meter (e.g. the user pans away
    // after the snap) instead of silently attributing its values to this job. Kept in a ref (not
    // state) since it is read from onLiveResult/the replay effect, never rendered. `passThrough`
    // marks the case where a job is matched but has no known meter number to check against (see
    // the populating effect below) — there, the guard accepts every result instead of dead-locking.
    const expectedMeterRef = useRef<ExpectedMeter | null>(null);

    const { speak, playEarcon, prime, muted, toggleMute } = useAudioFeedback(i18n?.language);

    // Developer-only tracker debug overlay; no customer-facing config flag on purpose.
    // Opt in via ...?aiDebug=1 (also works inside a hash route) or localStorage.aiReadingDebug='1'.
    const debugEnabled = useMemo(() => {
        try {
            return window.location.href.includes('aiDebug=1') || window.localStorage.getItem('aiReadingDebug') === '1';
        } catch (err) {
            return false;
        }
    }, []);
    // Tracker experiment settings (algorithm + point budget), switchable live from the debug
    // overlay (session-only, not persisted).
    const [trackerSettings, setTrackerSettings] = useState<TrackerSettings>(DEFAULT_TRACKER_SETTINGS);
    const onTrackerSettingsChange = useCallback((settings: TrackerSettings) => {
        setTrackerSettings(settings);
    }, []);

    // The portrait 3:4 target frame depends on the actual viewport aspect — recompute on
    // resize/rotation so the frame stays meter-shaped on every device.
    const [targetFrame, setTargetFrame] = useState<RelBox>(() => computeTargetFrame(visibleViewport()));
    useEffect(() => {
        const updateTargetFrame = () => setTargetFrame(computeTargetFrame(visibleViewport()));
        window.addEventListener('resize', updateTargetFrame);
        window.addEventListener('orientationchange', updateTargetFrame);
        return () => {
            window.removeEventListener('resize', updateTargetFrame);
            window.removeEventListener('orientationchange', updateTargetFrame);
        };
    }, []);

    const matchedJob = useMemo(() => jobs.find((j) => j.id === machine.jobId), [jobs, machine.jobId]);

    // Plausibility enforcement for the confirm step — identical to the manual flow: the flags are
    // job-merged (per-job config overrides the tenant config, like JobDetails.tsx:312-314), and
    // the same rules decide between warning, required comment/image and a strict-validation block.
    const applicationConfig = useSelector(applicationSelectors.getApplicationConfig);
    const confirmJobConfig = useMemo(() => mergeJobConfig(matchedJob?.config, applicationConfig), [matchedJob, applicationConfig]);
    const confirmFeatures = useMemo(
        () => resolveConfirmValidationFeatures(confirmJobConfig, matchedJob?.meter?.meterRateTasks ?? []),
        [confirmJobConfig, matchedJob],
    );
    // One reading hint per job, like the single comment field of the manual flow; sent with every
    // register result (JobDetails does the same, JobDetails.tsx:1606).
    const [confirmComment, setConfirmComment] = useState<string | undefined>(undefined);
    const accountId = matchedJob?.login ?? jobs[0]?.login;
    // Whether another OPEN job remains besides the one just read — drives the done dialog
    // ("capture another meter" vs "all readings done"). The just-submitted job is excluded by id
    // because `done` is only set by the server response (and never while offline).
    const hasOtherOpenJobs = useSelector(jobsSelectors.getHasOtherOpenJobs(machine.jobId ?? ''));

    const getToken = useCallback(
        () => new Promise<string>((resolve, reject) => dispatch(loginActions.getFreshToken(accountId, { resolve, reject }))),
        [dispatch, accountId],
    );

    // Anti-cheat identity guard (Task 13/follow-up): mirror the matched job's identity into
    // expectedMeterRef whenever machine.jobId resolves to a job — this covers METER_MATCHED and
    // CANDIDATE_CHOSEN alike, since both set machine.jobId. The derivation itself (job → fallback
    // → null, plus the passThrough carve-out) lives in deriveExpectedMeter (meterMatching.ts) and
    // is unit-tested there; see its doc comment for the full rationale.
    useEffect(() => {
        expectedMeterRef.current = deriveExpectedMeter(matchedJob, machine.meterNumber, trailingChars);
    }, [machine.meterNumber, machine.jobId, matchedJob, trailingChars]);

    // Initialize (or reset) the register accumulator once the job — and therefore its
    // meterRateTasks — is known. Gas/water meters have a single task, electricity may have HT+NT;
    // the accumulator is agnostic and simply expects whatever the job declares. Results buffered
    // during framing/identify are replayed into the fresh accumulator so values the AI already
    // recognized before the match are kept; when they alone complete all registers, the tariff
    // cycle step is skipped entirely.
    // Anti-cheat identity guard (Task 13): each buffered entry is checked against
    // expectedMeterRef (set by the effect above, which runs first on the same commit) before
    // replay — a value glimpsed from a different meter while still framing/identifying must not
    // enter the accumulator just because the job match landed afterwards.
    useEffect(() => {
        if (!matchedJob) {
            return;
        }
        let next = initAccumulator(matchedJob.meter?.meterRateTasks ?? []);
        const expected = expectedMeterRef.current;
        for (const { result, frame, frameRegion } of earlyResultsRef.current) {
            const identified = expected?.passThrough || (expected && resultIdentifiesMeter(result, expected.meterNumber, expected.trailingCharsCount));
            if (!identified) {
                continue;
            }
            next = accumulate(next, result, frame, frameRegion);
        }
        earlyResultsRef.current = [];
        setAccumulator(next);
        if (isComplete(next)) {
            dispatchMachine({ type: 'REGISTERS_COMPLETE' });
        }
        // Only matchedJob is a dependency: re-running this effect on every config change mid-flow
        // would wipe everything collected so far.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchedJob]);

    // Shared identify-step matching flow: tries a detected meter number (plus optional fallback
    // candidates) against the known jobs and dispatches the corresponding machine event. The
    // number may come from OCR or from the server-side DataMatrix/QR decode (confidence 1.0) —
    // both arrive through onLiveResult below.
    const matchMeterNumberAndDispatch = useCallback(
        (primary: string, fallbackCandidates: string[] = []) => {
            const attempts = [primary, ...fallbackCandidates];
            let matchResult = matchMeterNumberToJobs(primary, jobs, trailingChars ?? undefined);
            let matchedNumber = primary;
            for (let i = 1; matchResult.type === 'none' && i < attempts.length; i++) {
                matchResult = matchMeterNumberToJobs(attempts[i], jobs, trailingChars ?? undefined);
                matchedNumber = attempts[i];
            }

            if (matchResult.type === 'match') {
                noMatchCountRef.current = 0;
                dispatchMachine({ type: 'METER_MATCHED', jobId: matchResult.job.id, meterNumber: matchedNumber });
                return true;
            }
            if (matchResult.type === 'ambiguous') {
                noMatchCountRef.current = 0;
                dispatchMachine({ type: 'MATCH_AMBIGUOUS', candidates: matchResult.jobs.map((j) => j.meter.meterNumber) });
                return true;
            }
            return false;
        },
        [jobs, trailingChars],
    );

    // Step 3: live-detection result handling, one branch per step. capturedAt (present for live
    // frames, absent for gallery uploads) lets the tracker motion-compensate the stale boxes.
    // frameRegion (viewport-relative region frameBase64 covers) is set for high-res crop reads,
    // so the accumulator can store display boxes relative to the evidence image.
    const onLiveResult = useCallback(
        (result: RemoteResult | undefined, frameBase64: string, capturedAt?: number, frameRegion?: RelBox) => {
            // The tracked overlay frame belongs to the fast live loop (full-viewport frames,
            // frameRegion === undefined): its box is the accurate, continuously-polled source. A
            // hi-res crop read (frameRegion set) runs only for values/meter number; its box —
            // remapped from a padded crop — may differ substantially, so it may only refine the
            // frame when it still largely coincides with the last fast box (IoU gate), never replace
            // it outright. This is what stops the frame from jumping when a /detect crop read lands.
            const isHiRes = frameRegion !== undefined;

            // Relation is box geometry and is learned only from the fast loop. The display is
            // detected far more reliably than the meter housing; when both arrive, remember their
            // relative geometry so a display-only detection can still reconstruct the meter box.
            if (!isHiRes && result?.detectedMeterBox && result?.detectedDisplayBox) {
                meterDisplayRelationRef.current = computeBoxRelation(result.detectedMeterBox, result.detectedDisplayBox) ?? meterDisplayRelationRef.current;
            }
            let meterBox = result?.detectedMeterBox;
            if (!meterBox && result?.detectedDisplayBox && meterDisplayRelationRef.current) {
                meterBox = applyBoxRelation(result.detectedDisplayBox, meterDisplayRelationRef.current) ?? undefined;
            }

            if (!isHiRes) {
                // Fast loop owns the tracked box.
                if (meterBox || result?.detectedDisplayBox) {
                    lastFastBoxRef.current = meterBox ?? lastFastBoxRef.current;
                    setLastDetection({ meterBox, displayBox: result?.detectedDisplayBox, capturedAt });
                } else if (!result) {
                    // The fast loop explicitly saw no meter — drop the overlay box instead of
                    // letting a stale one linger while the user points elsewhere.
                    lastFastBoxRef.current = null;
                    setLastDetection(null);
                }
            } else if (shouldAdoptHiResBox(meterBox, lastFastBoxRef.current, AI_HIRES_BOX_ADOPT_MIN_IOU)) {
                // Hi-res crop read: refine the frame only when its box still coincides with the last
                // fast box; otherwise keep the fast box (a non-adopted hi-res result still feeds the
                // value/number logic below).
                setLastDetection({ meterBox, displayBox: result?.detectedDisplayBox, capturedAt });
            }

            // Values recognized before the job is known cannot be accumulated yet (the expected
            // registers come from the job); buffer them for the replay in the accumulator effect.
            if (readingGateRef.current && (machine.step === 'framing' || machine.step === 'identify') && (result?.meterRates?.length ?? 0) > 0) {
                const buffer = earlyResultsRef.current;
                buffer.push({ result: result as RemoteResult, frame: frameBase64, frameRegion });
                if (buffer.length > EARLY_RESULTS_BUFFER_SIZE) {
                    buffer.shift();
                }
            }

            switch (machine.step) {
                case 'framing': {
                    const meterVisible = !!result?.detectedMeterBox || !!result?.detectedDisplayBox || (result?.meterRates?.length ?? 0) > 0;
                    if (meterVisible) {
                        dispatchMachine({ type: 'METER_SEEN', stableFrames: config.stableFrames });
                    } else {
                        dispatchMachine({ type: 'METER_LOST' });
                    }
                    break;
                }
                case 'identify': {
                    // Reading gate: OCR numbers from far away are unreliable — wait for the gate.
                    if (!readingGateRef.current) {
                        break;
                    }
                    // Wrong-medium fast path: the meter clearly belongs to a medium the user has
                    // no job for ("gas meter, but only electricity is due"). This usually never
                    // yields a matching number, so waiting for number attempts or the identify
                    // timeout would leave the user aiming in silence — fail fast with the reason.
                    const detectedMedium = result?.medium;
                    if (detectedMedium && detectedMedium !== 'ABSTRACT') {
                        if (!jobsIncludeMedium(jobs, detectedMedium)) {
                            wrongMediumCountRef.current += 1;
                            lastNoMatchInfoRef.current = buildNoMatchInfo(result, jobs);
                            if (wrongMediumCountRef.current >= WRONG_MEDIUM_ATTEMPTS_BEFORE_FAILED) {
                                dispatchMachine({ type: 'MATCH_FAILED', info: lastNoMatchInfoRef.current });
                                break;
                            }
                        } else {
                            wrongMediumCountRef.current = 0;
                        }
                    }
                    const meterNumber = result?.meterNumber;
                    if (!meterNumber) {
                        break;
                    }
                    // OCR consensus: a number read from a single image may contain a confidently
                    // misread digit — only attempt the job match once the SAME number was read
                    // from `consensusCount` images in a row. (A server-side DataMatrix decode
                    // reports the same number on every frame, so it reaches consensus fastest.)
                    const normalized = normalizeMeterNumber(meterNumber);
                    const previous = ocrConsensusRef.current;
                    ocrConsensusRef.current =
                        previous?.number === normalized ? { number: normalized, count: previous.count + 1 } : { number: normalized, count: 1 };
                    if (ocrConsensusRef.current.count < config.consensusCount) {
                        break;
                    }
                    // Try the primary detected number first; if the AI also returned other id
                    // candidates (e.g. alternate OCR readings) and the primary number matches no
                    // job, retry with each candidate before counting this as a failed attempt.
                    const candidateNumbers = (result?.meterNumberCandidates ?? []).map((c) => c.id).filter(Boolean);
                    const matched = matchMeterNumberAndDispatch(meterNumber, candidateNumbers);
                    if (!matched) {
                        noMatchCountRef.current += 1;
                        lastNoMatchInfoRef.current = buildNoMatchInfo(result, jobs);
                        if (noMatchCountRef.current >= NO_MATCH_ATTEMPTS_BEFORE_FAILED) {
                            dispatchMachine({ type: 'MATCH_FAILED', info: lastNoMatchInfoRef.current });
                        }
                    }
                    break;
                }
                case 'tariffs':
                case 'read': {
                    if (!result || !readingGateRef.current) {
                        break;
                    }
                    // Anti-cheat identity guard: values are only ever accumulated from results
                    // that identify the MATCHED meter (by number or candidate id). A result
                    // without any readable number is rejected too — strict by product decision;
                    // see the spec's documented fallback if this proves too strict on-device.
                    // Exception: passThrough (matched job has no known meter number at all) lets
                    // everything through, since there is nothing to verify identity against.
                    const expected = expectedMeterRef.current;
                    const identified = expected?.passThrough || (expected && resultIdentifiesMeter(result, expected.meterNumber, expected.trailingCharsCount));
                    if (!identified) {
                        break;
                    }
                    setAccumulator((current) => {
                        const next = accumulate(current, result, frameBase64, frameRegion);
                        if (machine.step === 'tariffs' && isComplete(next)) {
                            dispatchMachine({ type: 'REGISTERS_COMPLETE' });
                        }
                        return next;
                    });
                    break;
                }
                default:
                    break;
            }
        },
        [machine.step, config.stableFrames, config.consensusCount, matchMeterNumberAndDispatch, jobs],
    );

    // Live loop runs while framing/identifying/collecting tariffs/reading, and only while
    // nothing is blocking progress (consent screens, permission errors, offline, etc.).
    const liveLoopEnabled = LIVE_LOOP_STEPS.has(machine.step) && !machine.blocker;
    useLiveDetection({
        videoRef,
        enabled: liveLoopEnabled,
        getToken,
        config: { pollIntervalMs: config.pollIntervalMs, liveTimeoutMs: config.liveTimeoutMs, frameDiffThreshold: config.frameDiffThreshold },
        source: trackerSettings.detectionSource,
        getPhase,
        onResult: onLiveResult,
        // Repeated failed detection requests are the robust offline signal (navigator.onLine and the
        // window events miss "WLAN up, server unreachable"). Map the transitions onto the machine.
        onConnectivityChange: (connectivity) => dispatchMachine({ type: connectivity === 'offline' ? 'WENT_OFFLINE' : 'BACK_ONLINE' }),
    });

    // Start-offline: if the flow reaches a live-loop step while already offline, surface the panel
    // immediately instead of waiting for the detection loop to rack up failures (or the framing
    // timeout to fire with "no meter found"). navigator.onLine false is a reliable *negative*.
    useEffect(() => {
        if (liveLoopEnabled && typeof navigator !== 'undefined' && navigator.onLine === false) {
            dispatchMachine({ type: 'WENT_OFFLINE' });
        }
    }, [liveLoopEnabled]);

    // Every fresh entry into 'identify' (including re-entry after RETRY_IDENTIFY or a blocker
    // resolving) starts a clean identification pass: the OCR number consensus and the
    // wrong-medium streak start over.
    useEffect(() => {
        if (machine.step !== 'identify' || machine.blocker) {
            return;
        }
        ocrConsensusRef.current = null;
        wrongMediumCountRef.current = 0;
    }, [machine.step, machine.blocker]);

    // Step 8: client-side box tracker interpolates between the ~1/s server detections. It anchors
    // on the meter box (the frame around the whole meter); the display box only serves as a
    // fallback when the AI returned no meter-level box.
    const trackerServerDetection = useMemo(() => {
        const box = lastDetection?.meterBox ?? lastDetection?.displayBox;
        return box ? { box, capturedAt: lastDetection?.capturedAt } : null;
    }, [lastDetection]);
    const {
        box: trackedBox,
        mode: trackerMode,
        debugInfo: trackerDebugInfo,
    } = useBoxTracker({
        videoRef,
        serverDetection: trackerServerDetection,
        enabled: TRACKER_STEPS.has(machine.step),
        minFps: config.trackerMinFps,
        onDegraded: () => dispatchMachine({ type: 'TRACKER_DEGRADED' }),
        debug: debugEnabled,
        algorithm: trackerSettings.algorithm,
        keypointCount: trackerSettings.keypointCount,
    });
    trackerModeRef.current = trackerMode;

    // The display box is carried along with the tracker's translation so the schematic display
    // overlay stays glued to the meter between server responses. The shift is measured from the
    // raw server meter box (same AI frame as the display box) to the currently displayed one.
    const liveDisplayBox = useMemo(() => {
        const displayBox = lastDetection?.displayBox;
        if (!displayBox) {
            return null;
        }
        if (trackedBox && trackerServerDetection) {
            return shiftBoxBy(displayBox, trackerServerDetection.box, trackedBox);
        }
        return displayBox;
    }, [lastDetection, trackedBox, trackerServerDetection]);

    // Guidance keeps ONE frame for the whole flow: the meter frame. Recognition works fine with
    // the meter held in it (verified in practice), so after the match the frame simply stays and
    // the instructions/label switch to "values are being captured — keep it steady".
    const inGuidanceSteps = TRACKER_STEPS.has(machine.step);
    const inCapturePhase = machine.step === 'tariffs' || machine.step === 'read';

    // Central alignment state: same score/hysteresis pair the AlignmentGuide runs internally
    // (same helpers, same inputs) — the screen needs it for the hard accent switch and (Task 10)
    // the reading gate.
    const alignmentScore = useMemo(
        () => (inGuidanceSteps && trackedBox ? computeAlignment(trackedBox, targetFrame).score : 0),
        [inGuidanceSteps, trackedBox, targetFrame],
    );
    const [alignmentSnapped, setAlignmentSnapped] = useState(false);
    useEffect(() => {
        setAlignmentSnapped((prev) => computeSnap(alignmentScore, prev));
    }, [alignmentScore]);
    const accentColor = inGuidanceSteps && !alignmentSnapped ? rampColor(emphasizedScore(alignmentScore)) : SNAPPED_ACCENT;
    // Reading gate opens earlier (0.7) than the visual snap (0.8): recognition warms up during
    // the final approach while the saturated-green snap stays the "perfectly placed" reward.
    // The gate runs on its OWN score (computeReadingGateScore), which — unlike the visual score —
    // never penalizes an oversized box: too close means large and sharp, the best case for
    // reading. Visually the user still sees "move back" without a snap, but values, meter number
    // and the hi-res detail reads keep flowing.
    const readingGateScore = useMemo(
        () => (inGuidanceSteps && trackedBox ? computeReadingGateScore(trackedBox, targetFrame) : 0),
        [inGuidanceSteps, trackedBox, targetFrame],
    );
    const [readingGateOpen, setReadingGateOpen] = useState(false);
    useEffect(() => {
        setReadingGateOpen((prev) => computeReadingGate(readingGateScore, prev));
    }, [readingGateScore]);
    // Reading gate ref: value/number extraction only runs while the meter actually sits in the
    // target frame — far-away crops are too blurry and only feed noise into the consensus. Box
    // detection and tracking stay ungated.
    readingGateRef.current = readingGateOpen;

    // Best value collected so far — shown inside the display overlay once a reading passed the
    // confidence threshold. Values still waiting for consensus render dimmed ("pending").
    const recognized = useMemo(() => {
        const collected = Object.values(accumulator.collected).filter((c) => c.value !== '');
        if (collected.length === 0) {
            return undefined;
        }
        const best = collected.reduce((a, b) => {
            if (a.accepted !== b.accepted) return a.accepted ? a : b;
            return b.confidence > a.confidence ? b : a;
        });
        return { value: best.value, accepted: best.accepted };
    }, [accumulator]);

    // Confirm step is stepped through ONE register at a time: the overlay shows the display photo
    // of the currently active register while its editable value sits in the panel below. The
    // per-register crops are cut from each register's evidence image once on entering confirm
    // (registers are final by then), so stepping between tariffs is instant and flicker-free.
    const [confirmCropsByTask, setConfirmCropsByTask] = useState<Record<string, string>>({});
    const [confirmIndex, setConfirmIndex] = useState(0);
    useEffect(() => {
        if (machine.step !== 'confirm') {
            setConfirmCropsByTask({});
            setConfirmIndex(0);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            const entries: Array<[string, string]> = [];
            for (const task of accumulator.expected) {
                const collected = accumulator.collected[task.taskId];
                if (!collected?.imageBase64 || !collected.displayBox) {
                    continue;
                }
                try {
                    const { displayCrop } = await cropImage(collected.imageBase64, collected.displayBox);
                    if (displayCrop) {
                        entries.push([task.taskId, displayCrop]);
                    }
                } catch (err) {
                    // No crop for this register — its step still shows the editable field.
                }
            }
            if (!cancelled) {
                setConfirmCropsByTask(Object.fromEntries(entries));
            }
        })();
        return () => {
            cancelled = true;
        };
        // Registers are final once confirm is entered; don't recrop on every value edit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [machine.step]);

    // Freeze the camera feed while the user checks the recognized values: the confirm overlay
    // shows a still display crop, and a live feed moving behind it is distracting and suggests
    // the assistant is still reading. Only the <video> element is paused (the MediaStream keeps
    // running, so resuming is instant); all capture loops are step-gated off in 'confirm' anyway.
    useEffect(() => {
        const video = videoRef.current;
        if (machine.step !== 'confirm' || !video) {
            return undefined;
        }
        video.pause();
        return () => {
            // Resume on leaving confirm (submit or unmount). play() on a muted MediaStream video
            // needs no user gesture; optional chaining covers jsdom, where play() returns nothing.
            video.play()?.catch(() => undefined);
        };
    }, [machine.step]);

    const activeConfirmTask = accumulator.expected[confirmIndex];
    const activeConfirmCrop = activeConfirmTask ? confirmCropsByTask[activeConfirmTask.taskId] : undefined;

    // High-res crop reads: once the tracked meter box is large enough on screen, a crop of it
    // (plus padding) is captured at native camera resolution every few seconds and evaluated.
    // Values feed the normal result path; the crop-relative boxes are mapped back to viewport
    // coordinates first, so overlay and tracker benefit from the sharper detection too.
    trackedBoxRef.current = trackedBox;
    const onLiveResultRef = useRef(onLiveResult);
    onLiveResultRef.current = onLiveResult;

    useEffect(() => {
        if (!HIRES_READ_STEPS.has(machine.step) || machine.blocker) {
            return undefined;
        }
        let cancelled = false;
        let timerId = 0;
        let activeController: AbortController | null = null;

        const scheduleNext = () => {
            if (cancelled) {
                return;
            }
            timerId = window.setTimeout(runOnce, AI_HIRES_CROP_INTERVAL_MS);
        };

        const runOnce = async () => {
            if (cancelled) {
                return;
            }
            const video = videoRef.current;
            const box = trackedBoxRef.current;
            if (!video || !box || !readingGateRef.current || box.w * box.h < AI_HIRES_MIN_BOX_AREA) {
                // Not framed for reading yet — recheck shortly (local state only, no network).
                scheduleNext();
                return;
            }
            const capture = captureViewportRegion(
                video,
                expandRelBox(box, AI_HIRES_CROP_PADDING_FACTOR),
                AI_HIRES_CROP_MAX_DIMENSION,
                AI_HIRES_CROP_JPEG_QUALITY,
                visibleViewport(),
            );
            if (!capture) {
                scheduleNext();
                return;
            }
            const capturedAt = performance.now();
            const controller = new AbortController();
            activeController = controller;
            const timeoutId = window.setTimeout(() => controller.abort(), abortAfterMs);
            try {
                const token = await getToken();
                const res = await aiApi.evaluateImage(controller, token, { image: capture.image });
                const result = RemoteResultHelper.mapImageEvaluationResponseToRemoteResult(res.data, { allowEmptyRates: true });
                if (result) {
                    onLiveResultRef.current(mapResultBoxesFromCrop(result, capture.region), capture.image, capturedAt, capture.region);
                }
            } catch (err) {
                // Crop read failed (timeout/network/token) — the small-frame live loop keeps
                // running, the next scheduled read simply tries again.
            } finally {
                window.clearTimeout(timeoutId);
                if (activeController === controller) {
                    activeController = null;
                }
                scheduleNext();
            }
        };

        scheduleNext();
        return () => {
            cancelled = true;
            window.clearTimeout(timerId);
            // Abort the in-flight crop read on step change/close — an orphaned 2560px request
            // keeps the detection service busy and delays the next run's first responses.
            activeController?.abort();
        };
    }, [machine.step, machine.blocker, getToken, abortAfterMs]);

    // Identify escape hatch: without any readable meter number the no-match counter never
    // increments, so this timeout is the only way out of an endless polling loop. MATCH_FAILED
    // surfaces the meter-not-found panel, whose blocker also pauses the live loop.
    useEffect(() => {
        if (machine.step !== 'identify' || machine.blocker) {
            return undefined;
        }
        // The timeout reports the most recent known failure reason (if any attempt got that far);
        // with nothing ever readable it stays the generic "could not be recognized". The short
        // tier applies once the reading gate is open (meter framed, hi-res reads running); a gate
        // transition rearms the timer with the matching tier.
        const timeoutMs = readingGateOpen ? IDENTIFY_GATED_TIMEOUT_MS : IDENTIFY_TIMEOUT_MS;
        const timeoutId = window.setTimeout(() => dispatchMachine({ type: 'MATCH_FAILED', info: lastNoMatchInfoRef.current ?? undefined }), timeoutMs);
        return () => window.clearTimeout(timeoutId);
    }, [machine.step, machine.blocker, readingGateOpen]);

    // Framing escape hatch: 10s without a single meter sighting stops the live loop (via the
    // blocker) and offers retry/exit. Keyed on stableCount on purpose: every METER_SEEN increments
    // it and every fresh METER_LOST resets it to 0, so any detection activity re-arms the timer —
    // it only fires after FRAMING_TIMEOUT_MS of complete silence. Repeated METER_LOST keeps the
    // value at 0 and does NOT re-arm.
    useEffect(() => {
        if (machine.step !== 'framing' || machine.blocker) {
            return undefined;
        }
        const timeoutId = window.setTimeout(() => dispatchMachine({ type: 'FRAMING_TIMEOUT' }), FRAMING_TIMEOUT_MS);
        return () => window.clearTimeout(timeoutId);
    }, [machine.step, machine.blocker, machine.stableCount]);

    const onRetryFraming = useCallback(() => dispatchMachine({ type: 'RETRY_FRAMING' }), []);

    // Retry from the noMatch panel: clear all failure counters and restart at framing so the user
    // can re-aim (typically at a different meter) with a clean slate.
    const onRetryIdentify = useCallback(() => {
        noMatchCountRef.current = 0;
        wrongMediumCountRef.current = 0;
        lastNoMatchInfoRef.current = null;
        dispatchMachine({ type: 'RETRY_IDENTIFY' });
    }, []);

    // Step 4: one full-resolution read per still-missing/low-confidence register. Runs once per
    // entry into the 'read' step; the full-res image replaces the live frame as evidence photo.
    useEffect(() => {
        if (machine.step !== 'read' || fullReadsStartedRef.current) {
            return undefined;
        }
        fullReadsStartedRef.current = true;

        const gaps = missingRegisters(accumulator);
        if (gaps.length === 0) {
            dispatchMachine({ type: 'FULL_READS_DONE' });
            return undefined;
        }

        let cancelled = false;
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), abortAfterMs);

        (async () => {
            // Reading gate: the one-shot read follows the same rule as the loops — no extraction
            // while the meter is not in the frame. The live/hi-res loops (both gated) keep
            // collecting once the user re-aims. FULL_READS_DONE below still fires unconditionally,
            // so skipping the capture only forfeits this one redundant attempt, not the step exit.
            if (!readingGateRef.current) {
                window.clearTimeout(timeoutId);
                if (!cancelled) {
                    dispatchMachine({ type: 'FULL_READS_DONE' });
                }
                return;
            }

            const video = videoRef.current;
            // Prefer a native-resolution crop of the tracked meter box; fall back to the full
            // (visible) frame when no box is being tracked at this moment.
            const box = trackedBoxRef.current;
            const cropCapture =
                video && box
                    ? captureViewportRegion(
                          video,
                          expandRelBox(box, AI_HIRES_CROP_PADDING_FACTOR),
                          AI_HIRES_CROP_MAX_DIMENSION,
                          AI_HIRES_CROP_JPEG_QUALITY,
                          visibleViewport(),
                      )
                    : null;
            const frame = cropCapture?.image ?? (video ? captureFrame(video, AI_IMAGE_MAX_DIMENSION, AI_CAMERA_SCREENSHOT_QUALITY, visibleViewport()) : null);

            if (frame) {
                try {
                    const token = await getToken();
                    const res = await aiApi.evaluateImage(controller, token, { image: frame });
                    if (!cancelled) {
                        const result = RemoteResultHelper.mapImageEvaluationResponseToRemoteResult(res.data);
                        // Anti-cheat identity guard: same rule as the live loop — a full-res read
                        // that landed on a different (or unidentifiable) meter than the matched one
                        // is discarded. FULL_READS_DONE below still fires unconditionally, so
                        // discarding here only forfeits this one attempt, not the step exit.
                        // passThrough (no known meter number for the matched job) accepts the result.
                        const expected = expectedMeterRef.current;
                        if (result && expected && (expected.passThrough || resultIdentifiesMeter(result, expected.meterNumber, expected.trailingCharsCount))) {
                            setAccumulator((current) => accumulate(current, result, frame));
                        }
                    }
                } catch (err) {
                    // Full-res read failed (timeout/network/token) — proceed regardless; the confirm
                    // panel lets the user fill in whatever is still missing.
                }
            }

            window.clearTimeout(timeoutId);
            if (!cancelled) {
                dispatchMachine({ type: 'FULL_READS_DONE' });
            }
        })();

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [machine.step]);

    useEffect(() => {
        if (machine.step !== 'read') {
            fullReadsStartedRef.current = false;
        }
    }, [machine.step]);

    // Step 5a: spoken step instructions, debounced so fast step cascades only announce the final
    // instruction. Blockers preempt (kind 'blocker') and get an error earcon as attention cue.
    useEffect(() => {
        if (machine.step === 'consent') {
            return undefined;
        }
        if (machine.blocker === 'noMatch' || machine.blocker === 'offline' || machine.blocker === 'framingTimeout') {
            playEarcon('error');
        }
        const kind = machine.blocker ? 'blocker' : 'instruction';
        const key = instructionKeyFor(machine.step, machine.blocker, machine.noMatch);
        // The on-screen noMatch text names the detected meter number; the spoken announcement
        // deliberately does not — a digit string read aloud is noise, the number is on screen.
        const spokenKey =
            key === 'aiReading.instructions.noMatchNumber'
                ? 'aiReading.instructions.noMatchNumberSpoken'
                : machine.step === 'done' && submitQueued
                  ? 'jobs.details.pendingSuccessHint'
                  : key;
        // `count` pluralizes the tariffs/confirm instructions ("Der Zählerstand wird gelesen" vs
        // "Die Zählerstände werden gelesen"). Sourced from the matched JOB, not the accumulator:
        // on the identify→tariffs transition the accumulator is initialized in the same commit
        // and would still read as empty here.
        const registerCount = matchedJob?.meter?.meterRateTasks?.length ?? accumulator.expected.length;
        let spokenText =
            (machine.step === 'tariffs' || machine.step === 'confirm') && !machine.blocker
                ? t(spokenKey, { count: registerCount })
                : t(spokenKey, instructionParamsFor(machine.noMatch, t));
        // The done announcement includes the account progress — ONE utterance so the two parts
        // can never drift apart (see the meter-matched announcement for the rationale).
        if (machine.step === 'done' && !machine.blocker) {
            spokenText += ` ${t(hasOtherOpenJobs ? 'aiReading.done.moreOpen' : 'aiReading.done.allDone')}`;
        }
        const timeoutId = window.setTimeout(() => speak(spokenText, kind), stepInstructionDelayMs(machine.step));
        return () => window.clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [machine.step, machine.blocker, machine.noMatch]);

    // Step 5b: SUCCESS feedback, tied to the actual events (not steps): tone first, a short beat,
    // then a feedback snippet — only then does the (debounced, queued) next instruction follow.
    // This is the "feedback before the next instruction" moment the flow was missing.
    const prevStepRef = useRef(machine.step);
    const completeCelebratedRef = useRef(false);
    useEffect(() => {
        const prev = prevStepRef.current;
        prevStepRef.current = machine.step;

        if (machine.step === 'framing' || machine.step === 'identify') {
            completeCelebratedRef.current = false;
        }
        if (prev === 'identify' && machine.step === 'tariffs') {
            playEarcon('success');
            const registersTotal = matchedJob?.meter?.meterRateTasks?.length ?? 0;
            const timeoutId = window.setTimeout(() => {
                // Multi-register meters (HT/NT): say up front how many values are expected, so
                // "meter recognized" is not mistaken for "already done". ONE utterance on purpose:
                // two queued snippets can drift seconds apart on iOS (unreliable onend + gaps),
                // making the tariff info arrive absurdly late. Skipped when the buffer replay
                // already completed everything — then "all captured" follows immediately.
                const announcement =
                    registersTotal > 1 && !isComplete(accumulator)
                        ? `${t('aiReading.feedback.meterMatched')} ${t('aiReading.feedback.multipleRegisters', { total: registersTotal })}`
                        : t('aiReading.feedback.meterMatched');
                speak(announcement, 'feedback');
            }, EARCON_TO_SPEECH_MS);
            return () => window.clearTimeout(timeoutId);
        }
        // "Alle Werte erfasst" only when the registers really are complete — the same transition
        // also fires via the 20s tariff timeout, where the claim would be wrong.
        if (prev === 'tariffs' && machine.step === 'read' && isComplete(accumulator)) {
            completeCelebratedRef.current = true;
            playEarcon('complete');
            const timeoutId = window.setTimeout(() => speak(t('aiReading.feedback.allValuesCaptured'), 'feedback'), EARCON_TO_SPEECH_MS);
            return () => window.clearTimeout(timeoutId);
        }
        // Late completion: a gap was only filled by the single full-res read in the 'read' step.
        // Celebrate once (tone only — the confirm instruction follows anyway).
        if (prev === 'read' && machine.step === 'confirm' && isComplete(accumulator) && !completeCelebratedRef.current) {
            completeCelebratedRef.current = true;
            playEarcon('complete');
        }
        return undefined;
        // accumulator is read only at the moment of the transition on purpose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [machine.step]);

    // Step 5c: intermediate success — every register value that reaches consensus ("accepted")
    // is acknowledged with a short tick (the buffered early-results replay produces at most one
    // collective tick); on multi-register meters the progress is also spoken ("Zählerstand 1 von
    // 2 erfasst"), so the user knows to keep holding. Completion itself is celebrated by the
    // transition effects above.
    const acceptedRegisterCountRef = useRef(0);
    useEffect(() => {
        const accepted = Object.values(accumulator.collected).filter((c) => c.accepted && c.value !== '').length;
        const previous = acceptedRegisterCountRef.current;
        acceptedRegisterCountRef.current = accepted;
        if (accepted > previous && (machine.step === 'tariffs' || machine.step === 'read') && !isComplete(accumulator)) {
            playEarcon('tick');
            if (accumulator.expected.length > 1) {
                speak(t('aiReading.feedback.registerCaptured', { captured: accepted, total: accumulator.expected.length }), 'feedback');
            }
        }
    }, [accumulator, machine.step, playEarcon, speak, t]);

    // Step 6: offline/online transitions feed the machine so it can block/unblock the flow.
    useEffect(() => {
        const handleOffline = () => dispatchMachine({ type: 'WENT_OFFLINE' });
        const handleOnline = () => dispatchMachine({ type: 'BACK_ONLINE' });
        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, []);

    const onCandidateChosen = useCallback(
        (meterNumber: string) => {
            dispatchMachine({ type: 'CANDIDATE_CHOSEN', meterNumber });
            // The candidates shown to the user are already full, distinct meter numbers, so the
            // re-match here MUST be exact (no trailingChars) — re-matching with trailingChars can
            // hit the same ambiguous set again when two jobs collide on the same trailing N chars,
            // which would re-surface the picker forever (soft deadlock). An exact match always
            // resolves to zero or one job, so this call terminates in METER_MATCHED or MATCH_FAILED,
            // never MATCH_AMBIGUOUS again.
            const matchResult = matchMeterNumberToJobs(meterNumber, jobs);
            if (matchResult.type === 'match') {
                dispatchMachine({ type: 'METER_MATCHED', jobId: matchResult.job.id, meterNumber });
            } else {
                // Exact resolution failed (pathological duplicate meter numbers or a stale
                // candidate) — fall back to the recoverable noMatch blocker (retry panel)
                // instead of leaving the machine stuck with no blocker.
                dispatchMachine({ type: 'MATCH_FAILED' });
            }
        },
        [jobs],
    );

    const onChangeValue = useCallback((taskId: string, value: string) => {
        setAccumulator((current) => ({
            ...current,
            collected: {
                ...current.collected,
                [taskId]: {
                    ...current.collected[taskId],
                    taskId,
                    value,
                    confidence: current.collected[taskId]?.confidence ?? 0,
                    imageBase64: current.collected[taskId]?.imageBase64 ?? '',
                    // A manual edit is the user's decision: accepted by definition, and marked
                    // with confirmations 0 so recognition results won't override it.
                    confirmations: 0,
                    accepted: true,
                },
            },
        }));
    }, []);

    // Step 7: submit — build MeterTaskResultType[] from whatever was collected/edited and dispatch
    // the existing job-results action. Offline queueing is handled entirely by the jobs saga.
    const submit = useCallback(() => {
        const job = jobs.find((j) => j.id === machine.jobId);
        if (!job || submitting) return;
        const results: MeterTaskResultType[] = accumulator.expected
            .map((task) => {
                const collected = accumulator.collected[task.taskId];
                if (!collected || collected.value === '') return undefined;
                return {
                    taskId: task.taskId,
                    readingValue: Number.parseFloat(collected.value),
                    readingImages: collected.imageBase64 ? [{ ...toReadingImagePayload(collected.imageBase64), aiResult: collected.aiMeta }] : [],
                    // The job-wide reading hint, attached to every register result exactly like
                    // the manual flow submits its single comment field (JobDetails.tsx:1606).
                    comment: confirmComment,
                } as MeterTaskResultType;
            })
            .filter((r): r is MeterTaskResultType => !!r);
        // Track BEFORE dispatching — the saga may answer synchronously. CONFIRMED follows the
        // outcome (see useSubmitOutcome above). Deliberately no earcon here: tones are reserved for
        // recognition successes (meter matched, value read) — pressing a button is not an event
        // worth a sound.
        trackSubmit(job.id);
        dispatch(jobsActions.sendMeterTaskResults(job.id, new Date().toISOString(), results));
    }, [jobs, machine.jobId, accumulator, confirmComment, dispatch, submitting, trackSubmit]);

    // Multi-register progress, shared by the on-screen progress line, the spoken feedback and
    // the stuck-capture timer below.
    const registersTotal = accumulator.expected.length;
    const registersCaptured = Object.values(accumulator.collected).filter((c) => c.accepted && c.value !== '').length;

    // Exit hatch for a stuck capture: the job is matched, but not all registers reach consensus
    // (dirty display, sun glare, …). After TARIFF_STUCK_PROMPT_MS without progress a non-blocking
    // panel offers to continue manually inside the job; every newly accepted register restarts
    // the timer, and "keep trying" arms it again. Recognition keeps running the whole time.
    const [captureStuck, setCaptureStuck] = useState(false);
    useEffect(() => {
        if (machine.step !== 'tariffs' || machine.blocker || captureStuck) {
            if (machine.step !== 'tariffs') {
                setCaptureStuck(false);
            }
            return undefined;
        }
        const timeoutId = window.setTimeout(() => setCaptureStuck(true), TARIFF_STUCK_PROMPT_MS);
        return () => window.clearTimeout(timeoutId);
        // registersCaptured restarts the timer: progress means we are not stuck.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [machine.step, machine.blocker, captureStuck, registersCaptured]);

    // Announce the stuck-capture exit once when it appears (event feedback, preempts the current
    // instruction) — the visible panel carries the same sentence.
    useEffect(() => {
        if (captureStuck) {
            speak(t('aiReading.stuck.description'), 'feedback');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [captureStuck]);

    // Jump into the matched job to finish manually. `replace` first: the assistant must not stay
    // on the history stack — back from the job details lands on the job list, not the camera.
    const onContinueInJob = useCallback(() => {
        dispatch(replace('/ablesung'));
        dispatch(push(`/ablesung/${machine.jobId}`));
    }, [dispatch, machine.jobId]);

    // Nothing recognizable at all — hand over to the classic job list (same stack rule).
    const onManualCapture = useCallback(() => {
        dispatch(replace('/ablesung'));
    }, [dispatch]);

    // "Try again" from the offline panel: clear the blocker so the live loop resumes. If the
    // connection is still down the next failed requests (or the navigator check) re-raise it.
    const onRetryConnection = useCallback(() => {
        dispatchMachine({ type: 'RETRY_CONNECTION' });
    }, []);

    // "Capture another meter" from the done dialog: clear everything job-/meter-specific and
    // restart at framing. The camera keeps running; consent stays given.
    const onNextMeter = useCallback(() => {
        earlyResultsRef.current = [];
        noMatchCountRef.current = 0;
        wrongMediumCountRef.current = 0;
        lastNoMatchInfoRef.current = null;
        acceptedRegisterCountRef.current = 0;
        lastFastBoxRef.current = null;
        setLastDetection(null);
        setAccumulator(initAccumulator([]));
        setConfirmComment(undefined);
        setSubmitQueued(false);
        dispatchMachine({ type: 'RESTART_FLOW' });
    }, []);

    const confirmValidation = useMemo(() => validateConfirm(accumulator, confirmComment, confirmFeatures), [accumulator, confirmComment, confirmFeatures]);

    // Confirm-step stepper navigation: advance one step, or submit after the last. The step
    // count includes the dedicated reading-hint step when the comment requirement is active.
    const isLastConfirm = confirmIndex >= confirmStepCount(accumulator, confirmValidation) - 1;
    const onConfirmNext = useCallback(() => {
        if (isLastConfirm) {
            submit();
        } else {
            setConfirmIndex((idx) => idx + 1);
        }
    }, [isLastConfirm, submit]);
    const onConfirmBack = useCallback(() => setConfirmIndex((idx) => Math.max(0, idx - 1)), []);

    // Snap earcon (the frame is reached): audible on iOS where navigator.vibrate is a no-op.
    // Throttled against threshold flutter and never on top of running speech.
    const lastSnapEarconRef = useRef(0);
    const onAlignmentSnap = useCallback(() => {
        const now = performance.now();
        if (now - lastSnapEarconRef.current < SNAP_EARCON_MIN_INTERVAL_MS) {
            return;
        }
        if (typeof window !== 'undefined' && window.speechSynthesis?.speaking) {
            return;
        }
        lastSnapEarconRef.current = now;
        playEarcon('snap');
    }, [playEarcon]);

    const started = machine.step !== 'consent';

    // ONE visible instruction at a time: during the camera phases the alignment hint below the
    // target frame is the instruction (the top area is regularly covered by browser permission
    // banners), and every blocker except offline brings its own panel. The top GuidancePanel text
    // therefore only shows when nothing else carries it (see GuidancePanel for the sr-only rest).
    const instructionVisible = (machine.step === 'cameraPermission' && !machine.blocker) || machine.blocker === 'offline';

    // Multi-register capture progress ("1 von 2 Zählerständen erfasst") under the alignment hint.
    const captureProgressText =
        inCapturePhase && registersTotal > 1 ? t('aiReading.progress.registers', { captured: registersCaptured, total: registersTotal }) : undefined;

    // Feature flag off -> the screen must be unreachable even via deep link (flag OFF = zero
    // behavior change). The camera-guided assistant is mobile-only, so it is likewise unreachable
    // on desktop even via deep link. Placed after all hooks above to respect the rules of hooks.
    if (!aiLiveAssistantEnabled || !isMobilePlatform) {
        return <Navigate to={'/'} replace={true} />;
    }

    return (
        <div className={'fixed inset-0 bg-black z-50'} onContextMenu={(e) => e.preventDefault()}>
            {started && machine.blocker !== 'permissionDenied' && (
                <Webcam
                    ref={(instance) => {
                        (webcamRef as any).current = instance;
                        videoRef.current = instance?.video ?? null;
                    }}
                    audio={false}
                    screenshotFormat={'image/jpeg'}
                    // The ideal resolution matters since captures are cover-cropped to the visible
                    // viewport (portrait phones only keep ~1/3 of a landscape frame) and the
                    // high-res crop reads want every native pixel on the meter. `ideal` 4K falls
                    // back gracefully to whatever the camera actually supports (often 1080p).
                    videoConstraints={{ facingMode: { ideal: 'environment' }, width: { ideal: 3840 }, height: { ideal: 2160 } }}
                    onUserMedia={() => dispatchMachine({ type: 'CAMERA_READY' })}
                    onUserMediaError={() => dispatchMachine({ type: 'CAMERA_DENIED' })}
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        // The feed is paused during 'confirm' (see the freeze effect): blur + darken
                        // the frozen frame so the display crop and the confirm panel stand out.
                        // Inline style (not Tailwind): filter utilities need JIT, which is off here.
                        // Display-only — canvas captures read the raw video, but nothing captures
                        // in 'confirm' anyway.
                        filter: machine.step === 'confirm' ? 'blur(6px) brightness(0.5)' : 'none',
                        transition: 'filter 300ms ease',
                    }}
                />
            )}
            {started && trackerSettings.effects.includes('blur') && TRACKER_STEPS.has(machine.step) && <FocusBlur box={trackedBox} />}
            {started && machine.blocker !== 'offline' && (
                <DetectionOverlay
                    meterBox={trackedBox}
                    displayBox={liveDisplayBox}
                    displayValue={machine.step === 'confirm' ? undefined : recognized?.value}
                    displayValuePending={recognized ? !recognized.accepted : false}
                    pendingLabel={t('aiReading.overlay.checking')}
                    displayImage={activeConfirmCrop}
                    meterLabel={machine.meterNumber}
                    mode={trackerMode}
                    accentColor={accentColor}
                    snapped={inGuidanceSteps ? alignmentSnapped : true}
                />
            )}
            {started && inGuidanceSteps && !machine.blocker && (
                <AlignmentGuide
                    box={trackedBox}
                    targetFrame={targetFrame}
                    searchHintKey={'aiReading.alignment.noMeter'}
                    progressText={captureProgressText}
                    effects={trackerSettings.effects}
                    frameColorMode={trackerSettings.frameColorMode}
                    frameLineStyle={trackerSettings.frameLineStyle}
                    onSnap={onAlignmentSnap}
                />
            )}
            {started && debugEnabled && (
                <TrackerDebugOverlay
                    debug={trackerDebugInfo}
                    serverBox={trackerServerDetection?.box ?? null}
                    trackedBox={trackedBox}
                    mode={trackerMode}
                    videoRef={videoRef}
                    settings={trackerSettings}
                    onSettingsChange={onTrackerSettingsChange}
                />
            )}
            {started && (
                <GuidancePanel
                    step={machine.step}
                    blocker={machine.blocker}
                    noMatch={machine.noMatch}
                    registersTotal={
                        (machine.step === 'tariffs' || machine.step === 'confirm') && !machine.blocker
                            ? matchedJob?.meter?.meterRateTasks?.length ?? registersTotal
                            : undefined
                    }
                    degraded={machine.degraded}
                    instructionVisible={instructionVisible}
                    muted={muted}
                    onToggleMute={toggleMute}
                />
            )}

            {machine.step === 'consent' && (
                <ConsentPanel
                    audioEnabled={!muted}
                    onAudioEnabledChange={(enabled) => {
                        if (enabled === muted) {
                            toggleMute();
                        }
                    }}
                    onStart={() => {
                        prime();
                        dispatchMachine({ type: 'CONSENT_GIVEN' });
                    }}
                />
            )}

            {machine.blocker === 'candidates' && <CandidatePicker candidates={machine.candidates} onChoose={onCandidateChosen} />}
            {machine.blocker === 'noMatch' && (
                <MeterNotFoundPanel info={machine.noMatch} onRetry={onRetryIdentify} onManualCapture={onManualCapture} onExit={exitAssistant} />
            )}

            {machine.blocker === 'offline' && <OfflinePanel onRetry={onRetryConnection} onExit={exitAssistant} />}

            {/* Framing timeout: the blocker already paused the live loop, so no frames are sent
                while this panel is up. Retry re-enters framing with a fresh 10s window. */}
            {machine.blocker === 'framingTimeout' && (
                <div className={'absolute bottom-0 left-0 right-0 bg-white rounded-t-xl p-4'}>
                    <h3>{t('aiReading.framingTimeout.title')}</h3>
                    <p data-testid="framing-timeout-description" className={'text-sm text-gray-600'}>
                        {t('aiReading.instructions.framingTimeout')}
                    </p>
                    <div className={'flex flex-col gap-2 mt-3'}>
                        <Button primary={true} block={true} onClick={onRetryFraming}>
                            {t('aiReading.buttons.retry')}
                        </Button>
                        <Button secondary={true} ghost={true} block={true} onClick={exitAssistant}>
                            {t('aiReading.buttons.exit')}
                        </Button>
                    </div>
                </div>
            )}

            {/* Camera denied: the assistant is camera-only by design (product decision
                2026-07-10, gallery upload removed) — the only way forward is the manual flow. */}
            {machine.blocker === 'permissionDenied' && (
                <div className={'absolute bottom-0 left-0 right-0 bg-white rounded-t-xl p-4 flex flex-col gap-2'}>
                    <p className={'text-sm text-gray-600'}>{t('aiReading.instructions.permissionDenied')}</p>
                    <Button primary={true} block={true} onClick={onManualCapture}>
                        {t('aiReading.buttons.manualEntry')}
                    </Button>
                </div>
            )}

            {/* Stuck-capture exit: non-blocking (recognition keeps running behind it). */}
            {machine.step === 'tariffs' && !machine.blocker && captureStuck && (
                <div className={'absolute bottom-0 left-0 right-0 bg-white rounded-t-xl p-4'}>
                    <h3>{t('aiReading.stuck.title')}</h3>
                    <p className={'text-sm text-gray-600'}>{t('aiReading.stuck.description')}</p>
                    <div className={'flex flex-col gap-2 mt-3'}>
                        <Button primary={true} block={true} onClick={onContinueInJob}>
                            {t('aiReading.buttons.continueInJob')}
                        </Button>
                        <Button secondary={true} block={true} onClick={() => setCaptureStuck(false)}>
                            {t('aiReading.buttons.keepTrying')}
                        </Button>
                        <Button secondary={true} ghost={true} block={true} onClick={exitAssistant}>
                            {t('aiReading.buttons.exit')}
                        </Button>
                    </div>
                </div>
            )}

            {machine.step === 'confirm' && (
                <ConfirmReadingsPanel
                    accumulator={accumulator}
                    activeIndex={confirmIndex}
                    features={confirmFeatures}
                    validation={confirmValidation}
                    comment={confirmComment}
                    onChangeComment={setConfirmComment}
                    onChangeValue={onChangeValue}
                    onNext={onConfirmNext}
                    onBack={onConfirmBack}
                    maskPostDecimals={maskPostDecimals}
                    meterType={resolveMeterType(matchedJob?.meter)}
                    submitting={submitting}
                />
            )}

            {machine.step === 'done' && (
                <DonePanel pending={submitQueued} hasOtherOpenJobs={hasOtherOpenJobs} onClose={exitAssistant} onNextMeter={onNextMeter} />
            )}

            {/* Same styling as the mute toggle in GuidancePanel: camera-overlay controls need an
                explicit dark scrim + white icon, independent of the (theme-driven) button styles. */}
            <div className={'absolute top-2 right-2'}>
                <button
                    type="button"
                    aria-label={t('aiReading.buttons.close')}
                    onClick={exitAssistant}
                    className={'bg-black bg-opacity-60 text-white rounded-full p-3'}
                >
                    <Icon path={Icon.Path.mdiClose} size={'22px'} />
                </button>
            </div>
        </div>
    );
};

export default AiReadingScreen;
